// Segmented control (DESIGN_SPEC §10.6): square, the chosen option gets a rule-strong box.
//   <Seg label options value onChange mode? describe? readOnly? className? />
//     options: { value, label, count?, disabled?, title? }[]
//     mode "radio" (default; role=radiogroup, roving tabindex, arrow keys / Home / End choose) | "toggle" (aria-pressed buttons)
//     describe(v) → the .seg-desc line under the control (Inter Tight); hovering or focusing another option previews it (.is-preview)
//     readOnly → only the chosen option, as a read-only radiogroup (e.g. an auto-detected value)
import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "./cx";

export type SegOption<T extends string> = { value: T; label: ReactNode; count?: ReactNode; disabled?: boolean; title?: string };

type SegProps<T extends string> = {
  label: string;
  options: readonly SegOption<T>[];
  value: T;
  onChange?: (value: T) => void;
  mode?: "radio" | "toggle";
  describe?: (value: T) => ReactNode;
  readOnly?: boolean;
  className?: string;
};

export function Seg<T extends string>({ label, options, value, onChange, mode = "radio", describe, readOnly, className }: SegProps<T>) {
  const [preview, setPreview] = useState<T | null>(null);
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const descId = useId();
  const shown = readOnly ? options.filter((o) => o.value === value) : options;
  const radio = mode === "radio" || readOnly;
  const enabled = shown.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
  const current = Math.max(0, shown.findIndex((o) => o.value === value));

  const choose = (i: number) => {
    const o = shown[i];
    if (!o || o.disabled || readOnly) return;
    onChange?.(o.value);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!radio || readOnly || !enabled.length) return;
    const at = enabled.indexOf(current);
    let next: number | undefined;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = enabled[(at + 1) % enabled.length];
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = enabled[(at - 1 + enabled.length) % enabled.length];
    else if (e.key === "Home") next = enabled[0];
    else if (e.key === "End") next = enabled[enabled.length - 1];
    if (next === undefined) return;
    e.preventDefault();
    choose(next);
    refs.current[next]?.focus();
  };

  const desc = describe ? describe(preview ?? value) : null;
  return (
    <>
      <div
        className={cx("seg", className)}
        role={radio ? "radiogroup" : "group"}
        aria-label={label}
        aria-readonly={readOnly || undefined}
        aria-describedby={describe ? descId : undefined}
        onKeyDown={onKeyDown}
      >
        {shown.map((o, i) => {
          const on = o.value === value;
          return (
            <button
              key={o.value}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role={radio ? "radio" : undefined}
              aria-checked={radio ? on : undefined}
              aria-pressed={radio ? undefined : on}
              tabIndex={radio ? (i === current ? 0 : -1) : undefined}
              disabled={o.disabled}
              title={o.title}
              className={readOnly ? "cursor-default" : o.disabled ? "pointer-events-none opacity-45" : undefined}
              onClick={() => choose(i)}
              onMouseEnter={() => !on && setPreview(o.value)}
              onMouseLeave={() => setPreview(null)}
              onFocus={() => setPreview(on ? null : o.value)}
              onBlur={() => setPreview(null)}
            >
              {o.label}
              {o.count !== undefined && o.count !== null && <span className="ct">{o.count}</span>}
            </button>
          );
        })}
      </div>
      {describe && (
        <p id={descId} className={cx("seg-desc", preview !== null && preview !== value && "is-preview")}>
          {desc}
        </p>
      )}
    </>
  );
}
